import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { assignUsersToCourse, searchUsersForAssignment, getEnrolledUsers } from '../../../services/adminService';

const defaultProfile = '/images/profile/default-profile.png';

function CourseAssignModal({ isOpen, onClose, course }) {
  const [users, setUsers] = useState([]);
  const [enrolledUserIds, setEnrolledUserIds] = useState(new Set());
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [validityDays, setValidityDays] = useState('90');
  const [pagination, setPagination] = useState({ total: 0, total_pages: 0, current_page: 1 });
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (isOpen && course) {
      setSelectedUserIds([]);
      setSearch('');
      setValidityDays(String(course.validity || course.course_duration || 90));
      fetchUsers('');
      fetchEnrolled();
    }
  }, [isOpen, course?.id]);

  const fetchEnrolled = async () => {
    try {
      const res = await getEnrolledUsers(course.id);
      if (res.success) {
        const ids = new Set((res.data.users || []).filter(u => !u.is_expired).map(u => u.user_id));
        setEnrolledUserIds(ids);
      }
    } catch (_) {}
  };

  const fetchUsers = async (query, page = 1) => {
    try {
      setLoading(true);
      const res = await searchUsersForAssignment({ q: query, limit: 20, page });
      if (res.success) {
        setUsers(res.data.users || []);
        setPagination(res.data.pagination || { total: 0, total_pages: 0, current_page: 1 });
      }
    } catch (_) {}
    finally { setLoading(false); }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => fetchUsers(val), 300);
  };

  const toggleUser = (userId) => {
    if (enrolledUserIds.has(userId)) return;
    setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const selectAll = () => {
    const selectable = users.filter(u => !enrolledUserIds.has(u.id)).map(u => u.id);
    const allSelected = selectable.every(id => selectedUserIds.includes(id));
    if (allSelected) {
      setSelectedUserIds(prev => prev.filter(id => !selectable.includes(id)));
    } else {
      setSelectedUserIds(prev => [...new Set([...prev, ...selectable])]);
    }
  };

  const handleAssign = async () => {
    if (selectedUserIds.length === 0) {
      toast.warn('Please select at least one user', { position: 'top-center' });
      return;
    }
    try {
      setAssigning(true);
      const res = await assignUsersToCourse({
        course_id: course.id,
        user_ids: selectedUserIds,
        validity_days: parseInt(validityDays) || 90,
      });
      if (res.success) {
        const d = res.data;
        toast.success(
          `Assigned to ${d.assigned_count} user(s)${d.renewed_count > 0 ? `, renewed ${d.renewed_count}` : ''}${d.already_enrolled > 0 ? ` (${d.already_enrolled} already enrolled)` : ''}`,
          { position: 'top-center', autoClose: 4000 }
        );
        onClose(true); // true = refresh parent
      } else {
        toast.error(res.data?.error_msg || 'Failed to assign course', { position: 'top-center' });
      }
    } catch (err) {
      toast.error('Failed to assign course', { position: 'top-center' });
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  const selectableOnPage = users.filter(u => !enrolledUserIds.has(u.id));
  const allPageSelected = selectableOnPage.length > 0 && selectableOnPage.every(u => selectedUserIds.includes(u.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => onClose(false)} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Assign Course</h3>
            <p className="text-sm text-gray-500 mt-0.5">{course?.course_name || course?.title || 'Course'}</p>
          </div>
          <button type="button" onClick={() => onClose(false)} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors">
            <Icon icon="mdi:close" width={22} height={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-gray-50 shrink-0">
          <div className="relative">
            <Icon icon="fluent:search-24-regular" width={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20 focus:border-[#7083FF]"
            />
          </div>
        </div>

        {/* User list */}
        <div className="flex-1 overflow-y-auto px-6 py-3 min-h-0">
          {/* Select all */}
          {selectableOnPage.length > 0 && (
            <button type="button" onClick={selectAll} className="text-xs font-medium text-[#7083FF] hover:underline mb-2">
              {allPageSelected ? 'Deselect all' : `Select all on this page (${selectableOnPage.length})`}
            </button>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-gray-200 border-t-[#7083FF]" />
            </div>
          ) : users.length === 0 ? (
            <p className="text-center py-8 text-gray-500 text-sm">No users found</p>
          ) : (
            <div className="space-y-1">
              {users.map(user => {
                const isEnrolled = enrolledUserIds.has(user.id);
                const isSelected = selectedUserIds.includes(user.id);
                return (
                  <label
                    key={user.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${
                      isEnrolled ? 'opacity-60 cursor-not-allowed bg-gray-50' : isSelected ? 'bg-[#7083FF]/5 border border-[#7083FF]/20' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected || isEnrolled}
                      disabled={isEnrolled}
                      onChange={() => toggleUser(user.id)}
                      className="w-4 h-4 rounded border-gray-300 text-[#7083FF] focus:ring-[#7083FF]/20"
                    />
                    <img src={user.profile_pic_url || defaultProfile} alt="" className="w-9 h-9 rounded-full object-cover border border-gray-100" onError={(e) => { e.target.src = defaultProfile; }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    {isEnrolled && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Enrolled</span>
                    )}
                  </label>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-gray-100">
              <button disabled={pagination.current_page <= 1} onClick={() => fetchUsers(search, pagination.current_page - 1)}
                className="px-3 py-1.5 text-xs bg-gray-100 rounded-lg disabled:opacity-40">Prev</button>
              <span className="text-xs text-gray-500">Page {pagination.current_page} / {pagination.total_pages}</span>
              <button disabled={pagination.current_page >= pagination.total_pages} onClick={() => fetchUsers(search, pagination.current_page + 1)}
                className="px-3 py-1.5 text-xs bg-gray-100 rounded-lg disabled:opacity-40">Next</button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50/50">
          <div className="flex items-center gap-4 mb-3">
            <label className="text-xs font-medium text-gray-600">Access expires in:</label>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="1"
                max="3650"
                value={validityDays}
                onChange={(e) => setValidityDays(e.target.value)}
                className="w-16 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#7083FF]/20"
              />
              <span className="text-xs text-gray-500">days</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {selectedUserIds.length > 0 ? (
                <><strong className="text-gray-900">{selectedUserIds.length}</strong> user{selectedUserIds.length !== 1 ? 's' : ''} selected</>
              ) : 'No users selected'}
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={() => onClose(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleAssign} disabled={assigning || selectedUserIds.length === 0}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-[#7083FF] rounded-xl hover:bg-[#5c6ef0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                {assigning ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Assigning...</>
                ) : (
                  <><Icon icon="fluent:person-add-24-regular" width={18} /> Assign Course</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseAssignModal;
